import App from 'antd/lib/app';
import { endOfMonth, startOfMonth } from 'date-fns';
import { omit, uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../../components/sidebar-filters/useSavedFiltersList';
import { usePrevious } from '../../../../components/usePrevious';
import { TSiteFilterKey } from '../../../../domain/saved-filters/interface';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from '../../../../domain/saved-filters/queries';
import { ISite } from '../../../../domain/site/interface';
import { useSiteListQuery } from '../../../../domain/site/queries';
import { IDateRange } from '../../../interface';
import {
    checkFilterInvalidValues,
    countNotEmptyFilters,
    parseSiteSans,
    siteToSelectOption,
    transformSitesSavedFilterValuesFromDto,
    transformSitesSavedFilterValuesToDto,
} from '../../../sites/sidebar/utils';
import { usePageLocation } from '../../../usePageState';
import { useSidebarFilterOptionsContext } from './SidebarFilterOptionsContext';

export interface IEnergyUsagePageState {
    // Filters
    control_provider_id?: number | number[];
    utility_customer_id?: number | number[];
    program_id?: number | number[];
    site_utility?: string | string[];
    status?: string | string[];
    market?: string | string[];
    caisoResource?: string | string[];

    // Options
    dispatch_type?: string;
    hasVen?: string;
    interval_data?: string;
    exist_salesforce_meter_enrollments?: string;
    exist_salesforce_meters?: string;
    isClosed?: string;

    // Used by whole page
    companyId?: number;
    date?: { start: number; end: number };

    sans?: string | string[];
}

export interface ISiteSanSelectOption {
    site: ISite;
    sans: {
        label: string;
        value: string;
    }[];
    values: string[];
}

export interface ISiteSidebarFilters {
    control_provider_id?: number[];
    utility_customer_id?: number[];
    program_id?: number[];
    site_utility?: string[];
    market?: string[];
    status?: string[];
    caisoResource?: string[];

    dispatch_type?: string | null;
    hasVen?: string | null;
    interval_data?: string | null;
    exist_salesforce_meter_enrollments?: string | null;
    exist_salesforce_meters?: string | null;
    isClosed?: string | null;
}

export interface IEnergyUsageSidebarFiltersContext {
    date: IDateRange;
    handleDateChange: (dateRange: IDateRange) => void;

    siteFiltersCount: number;
    siteFilters: ISiteSidebarFilters;
    savedFilters: ISavedFilter[];

    siteSelectOptions: ISiteSanSelectOption[]; // Options for site selection, used in the "Search" tab
    selectedSiteSans: string[];
    selectedSiteOptions: ISiteSanSelectOption[]; // Options for selected sites, used in the "Selected" tab

    isSitesLoading: boolean;
    isSelectedSiteOptionsLoading: boolean;
    isSavedFiltersLoading: boolean;

    handleSiteSelectChange: (siteSans: string[], checked: boolean, option?: ISiteSanSelectOption) => void;
    handleSiteFilterChange: (key: string, value: unknown) => void;
    handleSiteSelectAll: (newOptions: ISiteSanSelectOption[]) => void;
    handleSiteSelectClear: (clearedOptions: ISiteSanSelectOption[]) => void;
    handleSiteFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null; // Using for create/edit saved filter actions
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;
    handleRemoveFilterPropertyFromSelected: (propertyKey: TSiteFilterKey) => void;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const EnergyUsageSidebarFiltersContext = React.createContext<IEnergyUsageSidebarFiltersContext>({} as IEnergyUsageSidebarFiltersContext);

export const convertEnergyUsagePageStateToFilterState = (pageState: IEnergyUsagePageState): ISiteSidebarFilters => {
    const result: ISiteSidebarFilters = {};

    // Convert control_provider_id
    if (pageState.control_provider_id !== undefined) {
        result.control_provider_id = Array.isArray(pageState.control_provider_id) ? pageState.control_provider_id : [pageState.control_provider_id];
    }

    // Convert utility_customer_id
    if (pageState.utility_customer_id !== undefined) {
        result.utility_customer_id = Array.isArray(pageState.utility_customer_id) ? pageState.utility_customer_id : [pageState.utility_customer_id];
    }

    // Convert program_id
    if (pageState.program_id !== undefined) {
        result.program_id = Array.isArray(pageState.program_id) ? pageState.program_id : [pageState.program_id];
    }

    // Convert site_utility
    if (pageState.site_utility !== undefined) {
        result.site_utility = Array.isArray(pageState.site_utility) ? pageState.site_utility : [pageState.site_utility];
    }

    // Convert site_status
    if (pageState.status !== undefined) {
        result.status = Array.isArray(pageState.status) ? pageState.status : [pageState.status];
    }

    // Convert market_zone (handle as array even though it might be a single value)
    if (pageState.market !== undefined) {
        result.market = Array.isArray(pageState.market) ? pageState.market : [pageState.market];
    }

    // Convert resource
    if (pageState.caisoResource !== undefined) {
        result.caisoResource = Array.isArray(pageState.caisoResource) ? pageState.caisoResource : [pageState.caisoResource];
    }

    // Convert dispatch_type
    if (pageState.dispatch_type !== undefined) {
        result.dispatch_type = pageState.dispatch_type;
    }

    // Convert hasVen
    if (pageState.hasVen !== undefined) {
        result.hasVen = pageState.hasVen;
    }

    if (pageState.interval_data !== undefined) {
        result.interval_data = pageState.interval_data;
    }

    if (pageState.exist_salesforce_meter_enrollments !== undefined) {
        result.exist_salesforce_meter_enrollments = pageState.exist_salesforce_meter_enrollments;
    }

    if (pageState.exist_salesforce_meters !== undefined) {
        result.exist_salesforce_meters = pageState.exist_salesforce_meters;
    }

    if (pageState.isClosed !== undefined) {
        result.isClosed = pageState.isClosed;
    }

    return result;
};

export const EnergyUsageSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { date, handleDateChange } = useDateSelection();

    // Site Filtering and Site Selection
    const {
        isSelectedSiteOptionsLoading,
        isSitesLoading,
        siteFiltersCount,
        siteFilters,
        selectedSiteSans,
        selectedSiteOptions,
        siteSelectOptions,
        handleSiteSelectChange,
        handleSiteFilterChange,
        handleSiteFiltersChange,
        handleSiteSelectAll,
        handleSiteSelectClear,
        handleSiteFiltersClear,
    } = useSidebarFiltering();

    // Saving Site Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
        handleRemoveFilterPropertyFromSelected,
    } = useEnergyUsageSavedFilters();

    const onSiteFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleSiteFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when site filters change
        },
        [handleApplySavedFilter, handleSiteFilterChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleSiteFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleSiteFiltersChange]
    );

    const onSiteFiltersClear = useCallback(() => {
        handleSiteFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleSiteFiltersClear]);

    return (
        <EnergyUsageSidebarFiltersContext.Provider
            value={{
                date,
                handleDateChange,

                siteFiltersCount,
                siteFilters,
                savedFilters,

                isSitesLoading,
                isSelectedSiteOptionsLoading,
                isSavedFiltersLoading,

                handleSiteSelectChange,
                handleSiteFilterChange: onSiteFiltersChange,
                handleSiteFiltersClear: onSiteFiltersClear,
                handleSiteSelectAll,
                handleSiteSelectClear,

                siteSelectOptions,
                selectedSiteSans,
                selectedSiteOptions,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,
                handleRemoveFilterPropertyFromSelected,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </EnergyUsageSidebarFiltersContext.Provider>
    );
};

const defaultDate = {
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
};

const useDateSelection = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IEnergyUsagePageState = queryToState(location.search);

    const [date, setDate] = useState<IDateRange>({
        ...defaultDate,
        ...(pageState.date ? { start: new Date(pageState.date.start), end: new Date(pageState.date.end) } : {}),
    });

    const handleDateChange = (dateRange: IDateRange) => {
        setDate(dateRange);

        setPageQuery({ ...pageState, date: { start: dateRange.start.valueOf(), end: dateRange.end.valueOf() } });
    };

    return {
        date,
        handleDateChange,
    };
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IEnergyUsagePageState = queryToState(location.search);

    const companyId = pageState.companyId || null;
    const prevCompanyId = usePrevious(companyId);

    const [siteFilters, setSiteFilters] = useState<ISiteSidebarFilters>(convertEnergyUsagePageStateToFilterState(pageState));
    const siteFiltersCount = useMemo(() => countNotEmptyFilters(siteFilters), [siteFilters]);

    const { siteSelectOptions, isLoading: isSitesLoading } = useFilteredSiteOptions({
        siteFilters,
        siteFiltersCount,
        companyId,
    });

    const {
        isLoading: isSelectedSiteOptionsLoading,
        selectedSiteSans,
        setSelectedSiteSans,
        selectedSiteOptions,
        setSelectedSiteOptions,
        handleSiteSelectChange,
    } = useSelectedSiteOptions(parseSiteSans(pageState?.sans));

    const handleSiteFilterChange = (key: string, value: unknown) => {
        setSiteFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters
        const updatedPageState = { ...pageState, [key]: value };

        setPageQuery(updatedPageState);
    };

    const handleSiteFiltersChange = (newFilters: ISiteSidebarFilters) => {
        setSiteFilters(newFilters);

        setPageQuery({
            ...newFilters,
            companyId: companyId || undefined,
            date: pageState.date || undefined,
            sans: pageState.sans || undefined,
        });
    };

    const handleSiteSelectAll = (newOptions: ISiteSanSelectOption[]) => {
        // Merge new options with existing ones, ensuring uniqueness
        const updatedOptions = uniqBy([...selectedSiteOptions, ...newOptions], item => item.values.join(''));

        const updatedSiteSans = updatedOptions.flatMap(option => option.values);

        setSelectedSiteSans(updatedSiteSans);
        setSelectedSiteOptions(updatedOptions);
    };

    const handleSiteSelectClear = (clearedOptions: ISiteSanSelectOption[]) => {
        // Extract values (site IDs) from the options to be cleared
        const clearedOptionValues = clearedOptions.flatMap(option => option.values);

        const remainingSiteSans = selectedSiteSans.filter(san => !clearedOptionValues.includes(san));

        setSelectedSiteSans(remainingSiteSans);
        setSelectedSiteOptions(prevOptions => prevOptions.filter(option => option.values.every(item => !clearedOptionValues.includes(item))));

        setPageQuery({
            ...pageState,
            sans: remainingSiteSans.length > 0 ? remainingSiteSans : undefined,
        });
    };

    const handleSiteFiltersClear = () => {
        setSiteFilters({});

        setPageQuery({
            companyId: companyId || undefined,
            date: pageState.date || undefined,
            sans: pageState.sans || undefined,
        });
    };

    // Reset selected sites filters when company changes
    useEffect(() => {
        if (companyId === prevCompanyId) return;

        setSelectedSiteSans([]);
        setSelectedSiteOptions([]);
    }, [companyId, prevCompanyId, setSelectedSiteSans, setSelectedSiteOptions, setPageQuery, pageState]);

    return {
        isSitesLoading,
        isSelectedSiteOptionsLoading,

        siteFiltersCount,
        siteFilters,
        selectedSiteSans,
        selectedSiteOptions,
        siteSelectOptions,

        handleSiteSelectChange,
        handleSiteFilterChange,
        handleSiteFiltersChange,
        handleSiteFiltersClear,
        handleSiteSelectAll,
        handleSiteSelectClear,
    };
};

const useEnergyUsageSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.energyUsageDataAnalytics);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformSitesSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map(filter => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformSitesSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformSitesSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    const handleRemoveFilterPropertyFromSelected = useCallback(
        (propertyKey: TSiteFilterKey) => {
            if (!selectedSavedFilter) return;

            // Remove the property from the selected saved filter
            const updatedFilter = omit(selectedSavedFilter, `filter_values.${propertyKey}`);

            setSelectedSavedFilter(updatedFilter);
        },
        [selectedSavedFilter]
    );

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
        handleRemoveFilterPropertyFromSelected,
    };
};

const useSelectedSiteOptions = (initialSiteSans: string[]) => {
    const { notification } = App.useApp();
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IEnergyUsagePageState = queryToState(location.search);
    const [selectedSiteSans, setSelectedSiteSans] = useState<string[]>(initialSiteSans);
    const [selectedSiteOptions, setSelectedSiteOptions] = useState<ISiteSanSelectOption[]>([]);
    const [isInitialValuesProcessed, setIsInitialValuesProcessed] = useState<boolean>(false);

    // Has ids, but no options yet
    const shouldFetch = initialSiteSans.length > 0 && selectedSiteOptions.length === 0;
    const { data, isError, error, isInitialLoading } = useSiteListQuery(
        {
            filter: {
                sans: initialSiteSans.join(','),
            },
            pagination: {
                pageSize: 10000,
                current: 1,
            },
            include: 'company',
        },
        { enabled: shouldFetch }
    );

    if (isError) {
        notification.error({
            message: 'Error loading selected sites',
            description: error instanceof Error ? error.message : 'An unknown error occurred',
        });
    }

    const handleSiteSelectChange = (siteSans: string[], isAnyChildChecked: boolean, option?: ISiteSanSelectOption) => {
        setSelectedSiteSans(siteSans);

        setPageQuery({
            ...pageState,
            sans: siteSans.length > 0 ? siteSans : undefined,
        });

        if (!option) return;
        // Update selectedSiteOptions based on the checkbox state, to display selected sites in the "Selected" tab

        const updatedSelectedOptions = [...selectedSiteOptions];
        if (isAnyChildChecked) {
            const isOptionSelected = updatedSelectedOptions.map(item => item.site.site_id).includes(option.site.site_id);
            if (!isOptionSelected) updatedSelectedOptions.push(option);
        } else {
            const index = updatedSelectedOptions.findIndex(opt => opt.site.site_id === option.site.site_id);
            if (index !== -1) {
                updatedSelectedOptions.splice(index, 1);
            }
        }

        setSelectedSiteOptions(updatedSelectedOptions);
    };

    useEffect(() => {
        if (data && data.data && !isInitialValuesProcessed) {
            setIsInitialValuesProcessed(true);
            const options = data.data.map(site => siteToSelectOption(site));
            setSelectedSiteOptions(options);
            const allSiteValues = options.flatMap(opt => opt.values);

            setSelectedSiteSans(allSiteValues.filter(value => initialSiteSans.includes(value)));
        }
    }, [data, initialSiteSans, isInitialValuesProcessed]);

    return {
        isLoading: isInitialLoading,
        selectedSiteSans,
        setSelectedSiteSans,
        setSelectedSiteOptions,
        handleSiteSelectChange,
        selectedSiteOptions,
    };
};

interface IUseFilteredSiteOptionsParams {
    siteFilters: ISiteSidebarFilters;
    siteFiltersCount: number;
    companyId: number | null;
}

const useFilteredSiteOptions = ({ siteFilters, siteFiltersCount, companyId }: IUseFilteredSiteOptionsParams) => {
    const { notification } = App.useApp();

    const { data, isLoading, isError, error } = useSiteListQuery(
        {
            filter: siteFilters,
            ...(companyId && { company_id: companyId }),
            pagination: {
                pageSize: 10000,
                current: 1,
            },
            include: 'company',
        },
        { enabled: siteFiltersCount > 0 || !!companyId }
    );

    if (isError) {
        notification.error({
            message: 'Error loading sites',
            description: error instanceof Error ? error.message : 'An unknown error occurred',
        });
    }

    const siteSelectOptions: ISiteSanSelectOption[] = useMemo(() => {
        if (isLoading) return [];

        return (data?.data || []).map(site => siteToSelectOption(site));
    }, [data?.data, isLoading]);

    return {
        siteSelectOptions,
        isLoading,
    };
};

export const useEnergyUsageSidebarFiltersContext = () => {
    const context = React.useContext(EnergyUsageSidebarFiltersContext);
    if (!context) {
        throw new Error('useEnergyUsageSidebarFiltersContext must be used within EnergyUsageSidebarFiltersProvider');
    }
    return context;
};
